import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingInput from './FloatingInput';
import PrimaryButton from './PrimaryButton';
import { requestPasswordReset, verifyPasswordResetCode, resetPassword } from '../api/services/passwordResetService';
import dingoLogo from '../assets/img/logo/dingo-logo.png';

interface PasswordResetViewProps {
  onClose: () => void;
}

type ResetStep = 'email' | 'code' | 'password';

const PasswordResetView: React.FC<PasswordResetViewProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<ResetStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  
  // États pour les formulaires
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [canResendCode, setCanResendCode] = useState(false);
  const [resendTimer, setResendTimer] = useState(60); // 60 secondes
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.trim().length >= 8;
  };

  const passwordsMatch = (password1: string, password2: string) => {
    return password1.trim() === password2.trim();
  };

  const handleRequestCode = async () => {
    if (!validateEmail(email)) {
      setMessage({ text: 'Veuillez saisir une adresse email valide', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await requestPasswordReset(email);
    } catch (error: any) {
      // Ignorer l'erreur, continuer silencieusement
    } finally {
      setIsLoading(false);
    }
    
    // Toujours afficher le message de succès et passer à l'étape du code
    setMessage({ text: 'Code de vérification envoyé à votre email', type: 'success' });
    setCurrentStep('code');
    setCanResendCode(false);
    setResendTimer(60);
    
    // Effacer le message après 2 secondes
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  };

  const handleResendCode = async () => {
    if (!canResendCode) return;

    setIsLoading(true);
    setMessage(null);

    try {
      await requestPasswordReset(email);
    } catch (error: any) {
      // Ignorer l'erreur, continuer silencieusement
    } finally {
      setIsLoading(false);
    }
    
    // Toujours afficher le message de succès et redémarrer le timer
    setMessage({ text: 'Nouveau code envoyé à votre email', type: 'success' });
    setCanResendCode(false);
    setResendTimer(60);
    
    // Effacer le message après 2 secondes
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  };

  // Timer pour le renvoi de code
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentStep === 'code' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendCode(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentStep, resendTimer]);

  const handleVerifyCode = async () => {
    if (!code || code.length < 7) { // XXX-XXX = 7 caractères
      setMessage({ text: 'Veuillez saisir le code de vérification complet (XXX-XXX)', type: 'error' });
      return;
    }

    // Vérifier le format XXX-XXX
    const codeRegex = /^\d{3}-\d{3}$/;
    if (!codeRegex.test(code)) {
      setMessage({ text: 'Format de code invalide. Utilisez le format XXX-XXX', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await verifyPasswordResetCode(email, code);
      setMessage({ text: 'Code vérifié avec succès', type: 'success' });
      setCurrentStep('password');
    } catch (error: any) {
      setMessage({ text: error.message || 'Code incorrect', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Les mots de passe ne correspondent pas', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await resetPassword(email, code, newPassword);
      setShowSuccessModal(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Erreur lors de la modification du mot de passe', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderEmailStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié ?</h2>
        <p className="text-sm sm:text-base text-gray-600">Saisissez votre adresse email pour recevoir un code de vérification</p>
      </div>
      
      <FloatingInput
        label="Adresse email"
        name="email"
        type="email"
        placeholder="votre@email.com"
        value={email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
      />
      
      <PrimaryButton
        fullWidth
        onClick={handleRequestCode}
        disabled={!validateEmail(email) || isLoading}
        className="mt-4"
      >
        {isLoading ? 'Envoi en cours...' : 'Envoyer le code'}
      </PrimaryButton>
      
      <button
        onClick={onClose}
        className="w-full text-gray-500 hover:text-gray-700 text-sm"
      >
        Retour à la connexion
      </button>
    </div>
  );

  const renderCodeStep = () => {
    // Séparer le code en deux parties pour l'affichage
    const codeParts = code.split('-');
    const firstPart = codeParts[0] || '';
    const secondPart = codeParts[1] || '';

    const handleCodeChange = (part: 'first' | 'second', value: string) => {
      const newValue = value.replace(/[^0-9]/g, '').slice(0, 3);
      if (part === 'first') {
        const newCode = newValue + (secondPart ? `-${secondPart}` : '');
        setCode(newCode);
        // Auto-focus sur le deuxième champ si le premier est complet
        if (newValue.length === 3) {
          const secondInput = document.getElementById('code-second');
          if (secondInput) secondInput.focus();
        }
      } else {
        const newCode = (firstPart ? `${firstPart}-` : '') + newValue;
        setCode(newCode);
      }
    };

    return (
      <div className="space-y-6">
              <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Vérification</h2>
        <p className="text-sm sm:text-base text-gray-600">Saisissez le code reçu par email</p>
        <p className="text-xs sm:text-sm text-gray-500 mt-2">⏰ Le code expire dans 15 minutes</p>
      </div>
        
        <div className="flex items-center justify-center space-x-1 sm:space-x-2">
          {/* Premier groupe de 3 chiffres */}
          <div className="flex space-x-0.5 sm:space-x-2">
            {[0, 1, 2].map((index) => (
              <input
                key={`first-${index}`}
                type="text"
                maxLength={1}
                className="w-8 h-8 sm:w-12 sm:h-12 text-center text-sm sm:text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={firstPart[index] || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value) {
                    const newFirstPart = firstPart.split('');
                    newFirstPart[index] = value;
                    handleCodeChange('first', newFirstPart.join(''));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !firstPart[index]) {
                    // Aller au champ précédent
                    const prevInput = e.currentTarget.previousElementSibling as HTMLInputElement;
                    if (prevInput) prevInput.focus();
                  }
                }}
              />
            ))}
          </div>
          
          {/* Séparateur */}
          <div className="text-lg sm:text-2xl font-bold text-gray-400 mx-1 sm:mx-2">-</div>
          
          {/* Deuxième groupe de 3 chiffres */}
          <div className="flex space-x-0.5 sm:space-x-2">
            {[0, 1, 2].map((index) => (
              <input
                key={`second-${index}`}
                id={index === 0 ? 'code-second' : undefined}
                type="text"
                maxLength={1}
                className="w-8 h-8 sm:w-12 sm:h-12 text-center text-sm sm:text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={secondPart[index] || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value) {
                    const newSecondPart = secondPart.split('');
                    newSecondPart[index] = value;
                    handleCodeChange('second', newSecondPart.join(''));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !secondPart[index]) {
                    // Aller au champ précédent
                    const prevInput = e.currentTarget.previousElementSibling as HTMLInputElement;
                    if (prevInput) prevInput.focus();
                  }
                }}
              />
            ))}
          </div>
        </div>
        
        <PrimaryButton
          fullWidth
          onClick={handleVerifyCode}
          disabled={code.length < 7 || isLoading} // XXX-XXX = 7 caractères
          className="mt-4"
        >
          {isLoading ? 'Vérification...' : 'Vérifier le code'}
        </PrimaryButton>
        
        <div className="flex flex-col space-y-3">
          <button
            onClick={() => setCurrentStep('email')}
            className="w-full text-gray-500 hover:text-gray-700 text-sm"
          >
            Retour
          </button>
          
          <div className="text-center">
            {canResendCode ? (
              <button
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {isLoading ? 'Envoi...' : 'Renvoyer le code'}
              </button>
            ) : (
              <p className="text-xs text-gray-500">
                Renvoyer le code dans {resendTimer}s
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPasswordStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h2>
        <p className="text-sm sm:text-base text-gray-600">Choisissez votre nouveau mot de passe</p>
      </div>
      
      <FloatingInput
        label="Nouveau mot de passe"
        name="newPassword"
        type="password"
        placeholder="********"
        value={newPassword}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
      />
      
      <FloatingInput
        label="Confirmer le mot de passe"
        name="confirmPassword"
        type="password"
        placeholder="********"
        value={confirmPassword}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
      />
      
      <PrimaryButton
        fullWidth
        onClick={handleResetPassword}
        disabled={newPassword !== confirmPassword || !newPassword || !confirmPassword}
        className="mt-4"
      >
        {isLoading ? 'Modification...' : 'Modifier le mot de passe'}
      </PrimaryButton>
      
      <button
        onClick={() => setCurrentStep('code')}
        className="w-full text-gray-500 hover:text-gray-700 text-sm"
      >
        Retour
      </button>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 sm:p-6">
          <div className="text-center mb-6">
            <img src={dingoLogo} alt="OpenDingo" className="h-8 mx-auto mb-4" />
          </div>
          
          {message && (
            <div className={`mb-4 p-3 rounded-md text-center ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message.text}
            </div>
          )}
          
          {currentStep === 'email' && renderEmailStep()}
          {currentStep === 'code' && renderCodeStep()}
          {currentStep === 'password' && renderPasswordStep()}
        </div>
      </div>
      
      {/* Modal de succès */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Mot de passe réinitialisé avec succès !
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Votre mot de passe a été modifié. Vous pouvez maintenant vous reconnecter avec vos nouvelles informations.
              </p>
            </div>
            
            <PrimaryButton
              fullWidth
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
                navigate('/signin');
              }}
              className="mb-3"
            >
              Se connecter
            </PrimaryButton>
            
            <button
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
              }}
              className="w-full text-gray-500 hover:text-gray-700 text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PasswordResetView; 